// LIGHT OR DARK — each person's own choice, remembered with the person.
//
// THREE CHOICES, TWO STORED VALUES. "system" (follow the device) is the default and is
// stored as NULL on the user row (migration 066); "light" and "dark" are stored as
// themselves. Whatever the choice, it is resolved HERE into an explicit
// data-theme="light" | "dark" on <html>, and kept live: turn the device dark while
// following it and the page follows with no reload.
//
// WHO DECIDES, IN ORDER:
//   nobody logged in   the device, always — the login screen follows the device, and a
//                      previous person's choice never carries over to the next
//   logged in          their stored choice, or the device when they have none
//
// THE LOCAL COPY IS A CACHE FOR THE FIRST FRAME, never the truth. index.html paints from
// it before React loads, so a dark-mode user does not see a white flash while the session
// is asked who they are. It is written when a choice is known and cleared on logout.
import { createContext, useContext, useEffect, useState } from 'react';
import { authApi } from '../api';
import { useAuth } from './AuthContext';

export const THEMES = ['system', 'light', 'dark'];
// Read by the first-paint script in index.html — keep the key in step with it.
const CACHE = 'pressbooks.theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

const ThemeContext = createContext(null);

const writeCache = (choice) => {
  try {
    if (choice) window.localStorage.setItem(CACHE, choice);
    else window.localStorage.removeItem(CACHE);
  } catch { /* private mode: the cache is only a nicety */ }
};

export const ThemeProvider = ({ children }) => {
  const { partner, loading } = useAuth();
  const [choice, setChoice] = useState('system');

  // The person decides once there is one; before that, and after logout, the device does.
  useEffect(() => {
    if (loading) return;
    const next = partner ? partner.theme || 'system' : 'system';
    setChoice(next);
    writeCache(partner ? next : null);
  }, [partner, loading]);

  useEffect(() => {
    if (loading) return undefined; // the first-paint script is holding the frame
    const root = document.documentElement;
    const media = window.matchMedia ? window.matchMedia(DARK_QUERY) : null;
    const apply = () => {
      const dark = choice === 'dark' || (choice === 'system' && !!media && media.matches);
      root.setAttribute('data-theme', dark ? 'dark' : 'light');
    };
    apply();
    if (choice !== 'system' || !media) return undefined;
    media.addEventListener('change', apply);
    return () => media.removeEventListener('change', apply);
  }, [choice, loading]);

  // Applied at once, saved behind it. A save that fails puts the previous choice back, so
  // the screen never shows a theme the server will not return tomorrow.
  const setTheme = async (next) => {
    const previous = choice;
    setChoice(next);
    writeCache(next);
    try {
      await authApi.setTheme(next === 'system' ? null : next);
    } catch {
      setChoice(previous);
      writeCache(previous);
    }
  };

  return <ThemeContext.Provider value={{ theme: choice, setTheme }}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);
